// MODIFIED: Fixed type-mismatch in isDirty and synchronized Update state
import { useState, useEffect, useCallback, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import loadingSpinner from "../../../../../assets/loading.gif";
import { DateInput, Input } from "../../../../../common/Input";
import { ConfirmationModal } from "../../../../../common/Modal";
import PayrollPeriodPickerField from "../../../../../common/selectComponents/PayrollPeriodPicker";
import { IDENTIFIER } from "../../../../../utils/enums";

export function SalaryAdvanceLoanDetailModal({
  isOpen, onClose,
  selectedSar,
  onSave, onUpdate,
  loadingRequest,
  getLoanDetails,
  getDeductionAmountByCutoffCount,
  previewSchedule
}) {
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loanRecord, setLoanRecord] = useState(null);
  const [isOngoing, setIsOngoing] = useState(false);

  // States for Inputs
  const [deductionAmount, setDeductionAmount] = useState("");
  const [deductionFrequency, setDeductionFrequency] = useState("");
  const [payrollPeriodId, setPayrollPeriodId] = useState("");
  const [payrollPeriodName, setPayrollPeriodName] = useState("");

  // States for Backend Results
  const [numberOfDeductions, setNumberOfDeductions] = useState(0);
  const [schedule, setSchedule] = useState([]);
  const [lastGeneratedParams, setLastGeneratedParams] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const effectiveAmount = selectedSar?.approvedAmount ?? selectedSar?.amount;
  const isUsingFallback = !selectedSar?.approvedAmount;

  const isDirty = useMemo(() => {
    if (!lastGeneratedParams) return true;

    const amountChanged = Number(deductionAmount) !== Number(lastGeneratedParams.deductionAmount);
    const periodChanged = payrollPeriodId !== lastGeneratedParams.payrollPeriodId;
    const freqChanged = deductionFrequency !== lastGeneratedParams.deductionFrequency;

    return amountChanged || periodChanged || freqChanged;
  }, [deductionAmount, payrollPeriodId, deductionFrequency, lastGeneratedParams]);

  // MODIFIED: logic to sync draft loans with updated maintenance deduction settings
  const checkExistingLoan = useCallback(async () => {
    if (!selectedSar?.referenceNumber) return;
    setLoading(true);

    try {
      const { loan, schedule: existingSchedule } = await getLoanDetails(selectedSar.referenceNumber);

      if (loan) {
        const loanIsOngoing = loan.loanStatus === "Ongoing";
        setLoanRecord(loan);
        setIsOngoing(loanIsOngoing);
        setNumberOfDeductions(loan.totalInstallments);

        if (existingSchedule.length > 0) {
          const savedAmt = existingSchedule[0].amountToDeduct;
          const maintenanceAmt = selectedSar.deductionAmount;
          const initialFreq = loan.deductionFrequency;
          const initialPeriodId = loan.payrollPeriodId;
          const initialPeriodName = loan.payrollPeriodName;

          // If not ongoing and maintenance amount has changed, prioritize maintenance
          const amountToDisplay = (maintenanceAmt && Number(maintenanceAmt) !== Number(savedAmt))
            ? maintenanceAmt
            : savedAmt;

          setDeductionAmount(amountToDisplay);
          setPayrollPeriodId(initialPeriodId);
          setPayrollPeriodName(initialPeriodName);
          setDeductionFrequency(initialFreq);

          setSchedule(existingSchedule.map(item => ({
            index: item.installmentNumber,
            date: new Date(item.scheduledDate).toLocaleDateString(),
            amount: item.amountToDeduct
          })));

          // Set parameters to the database values to trigger the 'isDirty' validation
          setLastGeneratedParams({
            deductionAmount: savedAmt,
            payrollPeriodId: initialPeriodId,
            deductionFrequency: initialFreq
          });
        }
      } else {
        // Logic for new loan initialization
        setLoanRecord(null);
        setIsOngoing(false);
        setSchedule([]);
        setDeductionAmount("");
        setPayrollPeriodId("");
        setPayrollPeriodName("");
        setLastGeneratedParams(null);
      }
    } catch (error) {
      console.error("Error checking existing loan:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedSar, getLoanDetails]);

  useEffect(() => { if (isOpen) checkExistingLoan(); }, [isOpen, checkExistingLoan]);

  const handleGeneratePreview = async () => {
    if (!deductionAmount || deductionAmount <= 0 || !payrollPeriodId) return;

    setLoadingPreview(true);
    // Keep local references for the current values
    const currentAmt = deductionAmount;
    const currentPeriodId = payrollPeriodId;
    const currentFreq = deductionFrequency;

    const result = await previewSchedule({
      totalAmount: effectiveAmount,
      deductionAmount: parseFloat(currentAmt),
      deductionFrequency: currentFreq,
      payrollPeriodId: currentPeriodId
    });

    if (result) {
      setNumberOfDeductions(result.totalInstallments);
      setSchedule(result.schedule.map(s => ({
        index: s.installmentNumber,
        date: new Date(s.scheduledDate).toLocaleDateString(),
        amount: s.amountToDeduct
      })));

      setLastGeneratedParams({
        deductionAmount: currentAmt,
        payrollPeriodId: currentPeriodId,
        deductionFrequency: currentFreq
      });
    }
    setLoadingPreview(false);
  };

  const handleClose = () => {
    setDeductionAmount("");
    setNumberOfDeductions(0);
    setPayrollPeriodId("");
    setPayrollPeriodName("");
    setDeductionFrequency("");
    setSchedule([]);
    setLoanRecord(null);
    setIsOngoing(false);
    setShowConfirm(false);
    setLastGeneratedParams(null);
    onClose();
  };

  const handleConfirmSubmit = async () => {
    const payload = {
      salaryAdvanceRequestId: selectedSar.id,
      employeePersonalInformationId: selectedSar.employeePersonalInformationId,
      numberOfDeductions: parseInt(numberOfDeductions),
      deductionAmount: parseFloat(deductionAmount),
      payrollPeriodId,
      deductionFrequency,
    };

    const result = loanRecord ? await onUpdate(loanRecord.id, payload) : await onSave(payload);
    if (result?.success) handleClose();
    else setShowConfirm(false);
  };

  const handleSelectPayrollPeriod = async (payrollPeriod) => {
    setPayrollPeriodId(payrollPeriod.id);
    setPayrollPeriodName(payrollPeriod.name);

    const loanAmount = selectedSar.approvedAmount ?? selectedSar.amount;
    
    
    const computedDeductionResult = await getDeductionAmountByCutoffCount({
      employeePersonalInformationId: selectedSar.employeePersonalInformationId,
      loanAmount: loanAmount,
      payrollPeriodId: payrollPeriod.id,
      deductionFrequency: deductionFrequency
    });

    setDeductionAmount(computedDeductionResult?.deductionAmount ?? "");
  };

  if (!isOpen || !selectedSar) return null;

  const formatAmount = (n) =>
    Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <>
      <div role="dialog" className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto">
        <div className="bg-gray-50 w-full md:w-[90%] lg:w-[45%] rounded-lg shadow-xl flex flex-col relative max-h-[95vh]">

          <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 bg-white rounded-t-lg">
            <h2 className="text-xl font-semibold text-gray-500">{isOngoing ? "Loan Terms" : "Set Loan Terms"}</h2>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-700 bg-transparent border-none cursor-pointer"><CloseIcon /></button>
          </div>

          <div className="p-6 overflow-y-auto space-y-6">
            {loading ? (
              <div className="flex justify-center py-10"><img src={loadingSpinner} alt="loading" className="w-12 h-12" /></div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Reference" value={selectedSar.referenceNumber} isViewOnly={true} />
                  <Input label="Employee" value={selectedSar.employeeFullName} isViewOnly={true} />
                </div>

                <div className="border border-gray-300 rounded-lg bg-white p-4 shadow-sm relative">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={isUsingFallback ? "Total Loan (Requested Amount)" : "Total Loan (Approved Amount)"}
                      value={formatAmount(effectiveAmount)}
                      isViewOnly={true}
                      className={`bg-blue-50 font-bold`}
                    />
                    <Input label="Deduction / Cutoff" placeholder="eg. 5000" type="number" isDisabled={true} value={deductionAmount} onChange={(e) => setDeductionAmount(e.target.value)} />
                    <Input label="Calculated Installments" value={numberOfDeductions} isViewOnly={true} className="bg-gray-100" />

                    <div className="flex flex-col">
                      <label className="text-sm font-medium mb-1 text-gray-500">
                        Deduction Timing
                      </label>
                      <select
                        disabled={isOngoing}
                        value={deductionFrequency}
                        onChange={(e) => {
                          setDeductionFrequency(e.target.value);
                          setPayrollPeriodId("");
                          setPayrollPeriodName("");
                        }}
                        className="text-gray-500 border border-gray-300 rounded p-2"
                      >
                        <option value="" disabled hidden>
                          -- Select Deduction Timing --
                        </option>

                        <option value={IDENTIFIER.EVERYCUTOFF}>
                          Every Cutoff
                        </option>
                        <option value={IDENTIFIER.FIRST}>
                          First Cutoff
                        </option>
                        <option value={IDENTIFIER.SECOND}>
                          Second Cutoff
                        </option>
                      </select>
                    </div>

                    <PayrollPeriodPickerField
                      label="Start Payroll Period" // MODIFIED
                      payrollPeriodId={payrollPeriodId || ""}
                      payrollPeriodIdentifier={deductionFrequency}
                      displayValue={payrollPeriodName || "-- Select Payroll Period --"}
                      onSelect={(payrollPeriod) => handleSelectPayrollPeriod(payrollPeriod)}
                      onClear={() => {
                        setPayrollPeriodId("");
                        setPayrollPeriodName("");
                      }}
                      disabled={isOngoing || !deductionFrequency}
                    />
                    {!isOngoing && (
                      <div className="flex items-end">
                        <button
                          onClick={handleGeneratePreview}
                          disabled={loadingPreview || !deductionAmount || !payrollPeriodId}
                          className={`w-full py-2 rounded font-medium transition-all ${isDirty ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-md' : 'bg-green-100 text-green-700 cursor-default border border-green-200'
                            }`}
                        >
                          {loadingPreview ? "Calculating..." : isDirty ? "Generate Schedule" : "✓ Schedule Synced"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Warning/Info Message */}
                {!isOngoing && schedule.length > 0 && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-xs text-blue-700 leading-tight">
                          <strong>Note on Projected Dates:</strong> The repayment schedule below is calculated based on the selected frequency.
                          Actual deduction dates may adjust automatically to align with the official Payroll Periods once they are generated in the system.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="border rounded shadow-sm overflow-hidden">
                  <div className="bg-gray-50 p-2 border-b flex justify-between items-center">
                    <span className="font-medium text-sm text-gray-600">REPAYMENT PREVIEW</span>
                    {isDirty && !isOngoing && schedule.length > 0 && (
                      <span className="text-[10px] text-orange-600 font-bold animate-pulse uppercase tracking-tight">Sync Required</span>
                    )}
                  </div>
                  {schedule.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead className="bg-white">
                        <tr><th className="p-2 border-b">#</th><th className="p-2 border-b">Date</th><th className="p-2 border-b text-right">Amount</th></tr>
                      </thead>
                      <tbody>
                        {schedule.map(s => (
                          <tr key={s.index} className={`border-b transition-opacity ${isDirty && !isOngoing ? 'opacity-40' : 'opacity-100'}`}>
                            <td className="p-2 text-center">{s.index}</td>
                            <td className="p-2 text-center">{s.date}</td>
                            <td className="p-2 text-right font-mono">₱{Number(s.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-10 text-center text-gray-400 italic">Click "Generate Schedule" to see calculation</div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end p-4 border-t bg-white rounded-b-lg space-x-2">
            <button onClick={handleClose} className="px-4 py-2 bg-gray-400 text-white rounded">Cancel</button>
            {!isOngoing && (
              <button
                onClick={() => setShowConfirm(true)}
                disabled={loadingRequest || schedule.length === 0 || isDirty}
                className={`px-4 py-2 rounded font-bold text-white transition-colors ${isDirty ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600'}`}
              >
                {loanRecord ? "Update Draft" : "Save Draft"}
              </button>
            )}
          </div>
        </div>
      </div >

      <ConfirmationModal visible={showConfirm} onClose={() => setShowConfirm(false)} onConfirm={handleConfirmSubmit} title="Confirm Loan Schedule" message={`Save this ${numberOfDeductions}-installment schedule?`} confirmText="Yes, Save" type="warning" isLoading={loadingRequest} />
    </>
  );
}